<script>
import Layout from '@layouts/main.vue'
import {
  eventMethods,
  eventComputed,
  authComputed,
  registrationMethods,
  volunteerComputed,
  volunteerMethods,
} from '@state/helpers'
import dayjs from 'dayjs'

export default {
  page: {
    title: 'Kalendorius',
  },

  components: { Layout },

  data() {
    return {
      dateSelected: new Date(),
      year: new Date().getFullYear(),
      month: new Date().getMonth(),
      openEvents: [],
      dialogs: {
        invite: {
          visible: false,
          eventId: null,
          volunteerId: null,
        },
      },
    }
  },

  computed: {
    ...authComputed,

    ...eventComputed,

    ...volunteerComputed,

    dates() {
      if (!this.events) return

      let firstDay = new Date(this.year, this.month).getDay()
      if (firstDay === 0) firstDay = 7
      const lastDay = this.daysInMonth(this.year, this.month)

      let day = 1
      const result = []
      for (let weekNo = 0; weekNo < 6; weekNo += 1) {
        const week = []
        for (let dayNo = 0; dayNo < 7; dayNo += 1) {
          if (weekNo === 0 && dayNo < firstDay - 1) {
            week.push({ day: null, isToday: false, events: [] })
          } else {
            if (day <= lastDay) {
              const fullDate = `${this.year}-${this.month + 1}-${day}`
              const events = this.events.filter(
                ({ date }) => date === dayjs(fullDate).format('YYYY-MM-DD'),
              )
              const isToday =
                dayjs().format('YYYY-M-D') === fullDate && !events.length
              week.push({ day, isToday, events })
              day += 1
            }
          }
        }
        result.push(week)
      }

      return result
    },

    monthNames() {
      return [
        'Sausis',
        'Vasaris',
        'Kovas',
        'Balandis',
        'Gegužė',
        'Birželis',
        'Liepa',
        'Rugpjūtis',
        'Rugsėjis',
        'Spalis',
        'Lapkritis',
        'Gruodis',
      ]
    },

    monthNamesFormal() {
      return [
        'Sausio',
        'Vasario',
        'Kovo',
        'Balandžio',
        'Gegužės',
        'Birželio',
        'Liepos',
        'Rugpjūčio',
        'Rugsėjo',
        'Spalio',
        'Lapkričio',
        'Gruodžio',
      ]
    },

    dateTitle() {
      return `${this.year} ${this.monthNames[this.month]}`
    },

    dateSelectedStr() {
      return dayjs(this.dateSelected).format('YYYY-MM-DD')
    },
  },

  created() {
    const date = this.$route.query.date
    if (date) {
      this.dateSelected = new Date(dayjs(date))
      this.year = this.dateSelected.getFullYear()
      this.month = this.dateSelected.getMonth()
    }
    this.loadEventsList()
    this.fetchAllVolunteers({ sort: 'first_name', per_page: 10000 })
  },

  methods: {
    ...eventMethods,

    ...registrationMethods,

    ...volunteerMethods,

    loadEventsList() {
      this.fetchAllEvents({ date: this.year + '-' + (this.month + 1) }).then(
        () => {
          this.onClickDay(this.dateSelected.getDate())
        },
      )
    },

    daysInMonth(year, month) {
      return new Date(year, month + 1, 0).getDate()
    },

    onClickPrevWeek() {
      this.month -= 1
      if (this.month === -1) {
        this.month = 11
        this.year -= 1
      }
      this.loadEventsList()
    },

    onClickNextWeek() {
      this.month += 1
      if (this.month === 12) {
        this.month = 0
        this.year += 1
      }
      this.loadEventsList()
    },

    onClickDay(day) {
      const fullDate = `${this.year}-${this.month + 1}-${day}`
      this.openEvents = (this.events || []).filter(
        ({ date }) => date === dayjs(fullDate).format('YYYY-MM-DD'),
      )
      this.dateSelected = new Date(this.year, this.month, day)
    },

    onClickEventRegister(event_id) {
      if (confirm('Ar tikrai norite užsiregistruoti į šį renginį?')) {
        const data = {
          event_id,
          user_id: this.currentUser.id,
          status: 'registered',
        }
        if (this.currentUser.role === 'volunteer') {
          delete data.user_id
        }
        this.createRegistration(data).then(() => {
          this.loadEventsList()
        })
      }
    },

    onClickEventRegistrationDelete(eventID, id) {
      this.deleteRegistration({ eventID, id }).then(() => {
        this.loadEventsList()
      })
    },

    onClickEventRegistrationStatus(eventID, id, status) {
      this.updateRegistration({ eventID, id, status }).then(() => {
        this.loadEventsList()
      })
    },

    onClickEventAccept(event) {
      const registration = event.registrations.find(
        ({ email, status }) =>
          email === this.currentUser.email && status === 'invited',
      )

      if (registration) {
        this.updateRegistration({
          eventID: event.id,
          id: registration.id,
          status: 'registered',
        }).then(() => {
          this.loadEventsList()
        })
      }
    },

    onClickEventDecline(event) {
      const registration = event.registrations.find(
        ({ email, status }) => email === this.currentUser.email,
      )

      this.deleteRegistration({ eventID: event.id, id: registration.id }).then(
        () => {
          this.loadEventsList()
        },
      )
    },

    onClickInvite(event) {
      this.dialogs.invite.visible = true
      this.dialogs.invite.eventId = event.id
    },

    onSubmitInvite() {
      const payload = {
        event_id: this.dialogs.invite.eventId,
        user_id: this.dialogs.invite.volunteerId,
        status: 'invited',
      }
      this.createRegistration(payload).then(this.loadEventsList)
    },

    isSelectedDay(day) {
      const currentDate = new Date(this.year, this.month, day)
      const date = (d) => dayjs(d).format('YYYY-MM-DD')
      return date(currentDate) === date(this.dateSelected)
    },

    isEventInThePast(event) {
      return dayjs(event.date).format('YYYYMMDD') < dayjs().format('YYYYMMDD')
    },

    isRegistrationOpen(event) {
      if (this.isEventInThePast(event)) {
        return false
      }

      const alreadyRegistered = event.registrations.find(
        ({ email }) => email === this.currentUser.email,
      )
      return !alreadyRegistered
    },

    isInviteOpen(event) {
      if (this.isEventInThePast(event)) {
        return false
      }

      const alreadyInvited = event.registrations.find(
        ({ email, status }) => email === this.currentUser.email,
      )
      return !!alreadyInvited
    },

    openInviteStatus(event) {
      const alreadyInvited = event.registrations.find(
        ({ email, status }) => email === this.currentUser.email,
      )
      return alreadyInvited.status
    },

    nl2br(text) {
      return text.replace(/\n/g, '<br/>')
    },
  },
}
</script>

<template>
  <Layout>
    <base-page-title>
      <template v-slot:title>
        <h1 class="text-2xl font-bold"> Kalendorius </h1>
      </template>
      <template v-slot:actions>
        <div class="flex mb-4">
          <div>
            <div class="text-xl pt-2 mr-2">{{ dateTitle }}</div>
          </div>
          <div class="flex flex-column w-24">
            <base-btn class="mx-2 p-2" @click="onClickPrevWeek">
              <font-awesome-icon :icon="['fas', 'chevron-left']" />
            </base-btn>
            <base-btn class="p-2" @click="onClickNextWeek">
              <font-awesome-icon :icon="['fas', 'chevron-right']" />
            </base-btn>
          </div>
        </div>
        <div v-if="currentUser.role !== 'volunteer'" class="lg:ml-2">
          <base-btn
            class="input"
            @click="
              $router.push({
                name: 'event-create',
                query: { date: dateSelectedStr },
              })
            "
          >
            <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />
            Sukurti įvykį
          </base-btn>
        </div>
      </template>
    </base-page-title>

    <div class="block lg:flex h-full -mb-8">
      <div class="w-full lg:w-1/2 py-16 lg:py-0">
        <div
          class="w-full flex flex-row items-center justify-center overflow-y-auto"
          :class="{ 'h-full': openEvents.length === 0 }"
        >
          <div v-if="openEvents.length" class="w-full lg:w-3/4">
            <div class="text-center text-3xl mb-8 font-bold">
              {{ dateSelected.getFullYear() }}
              {{ monthNamesFormal[dateSelected.getMonth()] }}
              {{ dateSelected.getDate() }} d.
            </div>
            <div
              v-for="event in openEvents"
              :key="event.id"
              class="flex-1 mb-8"
            >
              <div class="text-2xl font-bold flex flex-column">
                <div class="flex-1">
                  <router-link
                    v-if="currentUser.role !== 'volunteer'"
                    :to="{
                      name: 'event-edit',
                      params: { id: event.id },
                    }"
                  >
                    {{ event.title }}
                  </router-link>
                  <template v-else>
                    {{ event.title }}
                  </template>
                </div>
                <div v-if="isRegistrationOpen(event)" class="w-24 text-right">
                  <base-btn-text
                    class="p-2 text-xs"
                    @click="onClickEventRegister(event.id)"
                  >
                    Registruotis
                  </base-btn-text>
                </div>
              </div>
              <div class="text-sm mb-2">
                <template v-if="event.city && event.place">
                  {{ event.city }}, {{ event.place }} -
                </template>
                <template v-if="event.city && !event.place">
                  {{ event.city }} -
                </template>
                {{ event.time }}
                <template v-if="event.duration">
                  (trukmė: {{ event.duration }} val.)
                </template>
              </div>
              <!-- eslint-disable vue/no-v-html -->
              <div class="mb-2" v-html="nl2br(event.description)" />
              <!-- eslint-enable vue/no-v-html -->
              <div class="mb-8">
                Sukūrė: {{ event.first_name }} {{ event.last_name }}
              </div>

              <div v-if="isInviteOpen(event)" class="mb-8">
                <h3
                  v-if="openInviteStatus(event) === 'invited'"
                  class="text-bold text-lg mb-2"
                >
                  Jūs buvote pakviesti. Ar dalyvausite?
                </h3>
                <h3
                  v-if="openInviteStatus(event) === 'registered'"
                  class="text-bold text-lg mb-2"
                >
                  Pakeisti dalyvavimo būseną:
                </h3>
                <div class="flex flex-column w-64">
                  <base-btn-text
                    v-if="openInviteStatus(event) !== 'registered'"
                    class="p-2 text-xs w-full"
                    @click="onClickEventAccept(event)"
                  >
                    Dalyvausiu
                  </base-btn-text>

                  <base-btn-text
                    class="p-2 text-xs btn-danger w-full"
                    :class="{ 'ml-4': openInviteStatus(event) === 'invited' }"
                    @click="onClickEventDecline(event)"
                  >
                    Nedalyvausiu
                  </base-btn-text>
                </div>
              </div>

              <h3
                v-if="event.registrations.length"
                class="text-bold text-lg mb-2"
              >
                Dalyviai
              </h3>

              <div
                v-for="(registration, index) in event.registrations"
                :key="registration.id"
                class="mb-1 bg-gray-100 p-2 rounded flex flex-column"
              >
                <div class="flex-1">
                  {{ index + 1 }})
                  {{ registration.first_name }}
                  {{ registration.last_name }}
                  <span
                    v-if="registration.status === 'invited'"
                    class="opacity-75"
                  >
                    (laukiama sutikimo)
                  </span>
                </div>
                <div
                  v-if="currentUser.role !== 'volunteer'"
                  class="flex flex-row"
                >
                  <div class="w-8 text-center">
                    <template v-if="registration.status === 'participated'">
                      <div
                        class="ml-2 px-2 py-1 text-xs"
                        title="Dalyvavo renginyje"
                      >
                        <font-awesome-icon
                          title="Dalyvavo"
                          :icon="['fas', 'check']"
                        />
                      </div>
                    </template>
                    <base-btn
                      v-else
                      type="secondary"
                      class="ml-2 px-2 py-1 text-xs"
                      title="Pažymėti, kad dalyvavo"
                      @click="
                        onClickEventRegistrationStatus(
                          event.id,
                          registration.id,
                          'participated', // eslint-disable-line
                        )
                      "
                    >
                      <font-awesome-icon :icon="['fas', 'check']" />
                    </base-btn>
                  </div>
                  <div class="w-8 ml-4" title="Pašalinti rezervaciją">
                    <base-btn
                      type="danger"
                      class="px-2 py-1 text-xs"
                      @click="
                        onClickEventRegistrationDelete(
                          event.id,
                          registration.id, // eslint-disable-line
                        )
                      "
                    >
                      <font-awesome-icon :icon="['fas', 'minus']" />
                    </base-btn>
                  </div>
                </div>
              </div>

              <div
                v-if="currentUser.role !== 'volunteer'"
                class="mt-4 w-full lg:w-64"
              >
                <base-btn-text
                  class="text-xs w-full btn-main input"
                  @click="onClickInvite(event)"
                >
                  Pakviesti dalyvauti
                </base-btn-text>
              </div>
            </div>
          </div>
          <template v-else>
            <div>
              <div class="text-center text-3xl mb-2 font-bold w-full">
                {{ dateSelected.getFullYear() }}
                {{ monthNames[dateSelected.getMonth()] }}
                {{ dateSelected.getDate() }} d.
              </div>
              <div class="opacity-75 text-center w-full mb-4">
                Jokių įvykių šiai dienai nėra.
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="w-full lg:w-1/2 pb-8">
        <div
          class="bg-gray-100 rounded inline-block p-8 w-full flex h-full flex-row items-center justify-center"
        >
          <table>
            <tr>
              <td class="text-center text-gray-500 text-sm">P</td>
              <td class="text-center text-gray-500 text-sm">A</td>
              <td class="text-center text-gray-500 text-sm">T</td>
              <td class="text-center text-gray-500 text-sm">K</td>
              <td class="text-center text-gray-500 text-sm">P</td>
              <td class="text-center text-gray-500 text-sm">Š</td>
              <td class="text-center text-gray-500 text-sm">S</td>
            </tr>
            <tr v-for="(week, weekIndex) in dates" :key="weekIndex">
              <td v-for="(day, dayIndex) in week" :key="dayIndex">
                <div
                  class="p-4 rounded cursor-pointer text-center"
                  :class="{
                    'bg-gray-200 font-bold':
                      day.isToday || isSelectedDay(day.day),
                    'bg-yellow-400': day.events.length,
                  }"
                  @click="onClickDay(day.day)"
                >
                  {{ day.day }}
                </div>
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <base-dialog
      v-if="dialogs.invite.visible"
      title="Pridėti savanorį"
      :overlay-closes="true"
      @close="dialogs.invite.visible = false"
    >
      <base-label-select
        v-model="dialogs.invite.volunteerId"
        :options="volunteerOptions"
        class="w-full mt-4"
        label="Pakviesti savanorį"
        required
      >
      </base-label-select>

      <template v-slot:buttons>
        <div class="w-48 relative cursor-pointer ml-4">
          <base-btn class="p-3" @click="onSubmitInvite">
            Pridėti savanorį
          </base-btn>
        </div>
      </template>
    </base-dialog>
  </Layout>
</template>
